import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { progressApi } from '@/api/progress';
import { homeworkTitleLines } from '../homeworkTitles';

import { StudentSideWidgets } from './StudentSideWidgets';

/**
 * Правая колонка переписки: что куратор сейчас проверяет, плюс общие виджеты.
 *
 * ⚠️ КАРТОЧКИ КУРАТОРА И ЗАГОТОВОК ОТВЕТОВ ЗДЕСЬ НЕТ — обе убраны 07.09.2026 по
 * просьбе заказчика, причины описаны ниже по месту. Колонка осталась узкой
 * намеренно: главное на этом экране — сама переписка, а не то, что вокруг неё.
 *
 * ⚠️ Подписи вроде «IELTS 8.0 · 4 года стажа» из макета здесь НЕТ и не будет:
 * анкета куратора ученику не отдаётся (`/curator/profile/me` — для самого
 * куратора). Придумывать квалификацию человека нельзя.
 */
export function ChatSidebar({ marathonIds }: { marathonIds: string[] }) {
  const { t, i18n } = useTranslation();

  // Работы, которые куратор ещё не проверил.
  const homework = useQuery({
    queryKey: ['student', 'homework', 'pending'],
    queryFn: () => progressApi.listMyHomework('pending', 0, 5),
  });
  const pending = homework.data?.items ?? [];

  return (
    <div className="space-y-3.5">
      {/* ⚠️ КАРТОЧКИ КУРАТОРА ЗДЕСЬ БОЛЬШЕ НЕТ, И ЭТО НЕ ПОТЕРЯ. Заказчик
          07.09.2026: «уберём отсюда виджет "твой куратор" — потому что сверху
          чата же итак написано». Он прав: имя и фото собеседника стоят в шапке
          переписки, а карточка повторяла их в полуметре правее. */}

      {pending.length > 0 && (
        <>
          <div className="mx-0.5 mb-2.5 mt-[18px] flex items-baseline justify-between">
            <h3 className="font-display text-[15.5px] font-bold text-on-surface">{t('chat.awaitingReview')}</h3>
            <span className="text-[12.5px] font-semibold text-on-surface-variant tabular-nums">{pending.length}</span>
          </div>
          <div className="rounded-[20px] bg-surface-container px-[18px] py-0.5">
            {pending.map((hw) => (
              <div key={hw.id} className="flex items-center gap-3 border-b border-outline py-3 last:border-b-0">
                <span className="grid h-[34px] w-[34px] flex-none place-items-center rounded-xl bg-primary-container">
                  <span className="material-symbols-outlined text-[17px] text-primary">assignment</span>
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-semibold text-on-surface">
                    {homeworkTitleLines(hw, t('hw.rowTitle')).title}
                  </span>
                  <span className="block text-[11.5px] text-on-surface-variant">
                    {t('hw.sentOn', {
                      date: new Date(hw.submitted_at).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long' }),
                    })}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ⚠️ «БЫСТРЫЕ ОТВЕТЫ» УБРАНЫ ПО ПРОСЬБЕ ЗАКАЗЧИКА (07.09.2026). Три
          заготовки — «Понял», «Можно перенести?», «Не получается» — занимали
          место постоянно, а годились в редкий разговор. Написать это руками
          быстрее, чем найти нужную кнопку среди трёх. */}

      {/* ⚠️ ТЕ ЖЕ ВИДЖЕТЫ, ЧТО В ОСТАЛЬНЫХ РАЗДЕЛАХ, И ОБЩИМ КОДОМ.
          Заказчик 07.09.2026 попросил их и сюда.

          ⚠️ ИДУТ ПОСЛЕДНИМИ, А НЕ ПЕРВЫМИ. В остальных разделах первым стоит
          куратор — здесь он уже есть выше, и своей карточкой, и собеседником в
          переписке. Поставить общий блок наверх значило бы показать кнопку
          «Написать куратору» человеку, который ему прямо сейчас пишет. */}
      <StudentSideWidgets marathonIds={marathonIds} withCurator={false} />
    </div>
  );
}
